/**
 * LiveKit Interview Hook
 * 
 * Manages LiveKit room connection and track publishing for interview sessions.
 * This hook handles:
 * - Connecting to LiveKit room with token
 * - Publishing camera+audio track
 * - Publishing screen share track
 * - Managing track lifecycle
 * - Providing video refs for previews
 * 
 * Based on POC implementation adapted to Invirtus architecture.
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { Room, Track } from 'livekit-client';

export interface UseLiveKitInterviewOptions {
  roomName?: string;
  token?: string;
  livekitUrl?: string;
  autoConnect?: boolean;
}

export interface LiveKitMediaStatus {
  isConnected: boolean;
  isCameraPublished: boolean;
  isScreenPublished: boolean;
  isAudioPublished: boolean;
}

/**
 * Hook for managing LiveKit room connection and track publishing
 */
export function useLiveKitInterview(options: UseLiveKitInterviewOptions = {}) {
  const {
    roomName,
    token,
    livekitUrl,
    autoConnect = false,
  } = options;

  // Create room instance (only once)
  const [room] = useState(() => new Room({
    adaptiveStream: true,
    dynacast: true,
    audioCaptureDefaults: {
      sampleRate: 16000,
      noiseSuppression: true,
      voiceIsolation: true,
      echoCancellation: true,
    }
  }));

  // State
  const [mediaStatus, setMediaStatus] = useState<LiveKitMediaStatus>({
    isConnected: false,
    isCameraPublished: false,
    isScreenPublished: false,
    isAudioPublished: false,
  });

  // The mic MediaStreamTrack, surfaced so callers (e.g. the realtime
  // transcription fallback) can tap it without duplicating
  // getUserMedia. Cleared on stopCamera/disconnect.
  const [cameraAudioTrack, setCameraAudioTrack] = useState<MediaStreamTrack | null>(null);

  // Refs for local media streams (for preview)
  const cameraStreamRef = useRef<MediaStream | null>(null);
  const screenStreamRef = useRef<MediaStream | null>(null);

  // Refs for video elements (for preview)
  const cameraVideoRef = useRef<HTMLVideoElement>(null);
  const screenVideoRef = useRef<HTMLVideoElement>(null);

  // Track if component is mounted
  const isMountedRef = useRef(true);

  // Effect for mount/unmount tracking
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  /**
   * Connect to LiveKit room
   */
  const connectToRoom = useCallback(async (): Promise<boolean> => {
    if (!livekitUrl || !token || !roomName) {
      console.error('[useLiveKitInterview] Missing connection credentials', { livekitUrl, token, roomName });
      return false;
    }

    if (room.state === 'connected') {
      console.log('[useLiveKitInterview] Already connected to room');
      return true;
    }

    try {
      console.log('[useLiveKitInterview] 🔌 Connecting to LiveKit room...', { roomName, livekitUrl });
      
      await room.connect(livekitUrl, token);
      
      if (!isMountedRef.current) return false;
      
      console.log('[useLiveKitInterview] ✓ Connected to LiveKit room');
      
      setMediaStatus(prev => ({ ...prev, isConnected: true }));
      
      return true;
    } catch (error) {
      console.error('[useLiveKitInterview] Failed to connect to room:', error);
      return false;
    }
  }, [room, livekitUrl, token, roomName]);

  /**
   * Publish camera + audio tracks
   */
  const publishCamera = useCallback(async (): Promise<boolean> => {
    if (!room || room.state !== 'connected') {
      console.error('[useLiveKitInterview] Cannot publish camera: Not connected to room');
      return false;
    }

    try {
      console.log('[useLiveKitInterview] 📹 Requesting camera + audio access...');
      
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 1280 },
          height: { ideal: 720 },
          frameRate: { ideal: 15 }
        },
        audio: true
      });

      if (!isMountedRef.current) {
        stream.getTracks().forEach(track => track.stop());
        return false;
      }

      console.log('[useLiveKitInterview] ✓ Camera + audio access granted');
      
      // Store stream for preview
      cameraStreamRef.current = stream;
      
      // Attach to video element for preview
      if (cameraVideoRef.current) {
        cameraVideoRef.current.srcObject = stream;
        cameraVideoRef.current.muted = true;
        await cameraVideoRef.current.play().catch(e => console.warn('Could not play camera preview:', e));
      }

      // Publish video track
      const videoTrack = stream.getVideoTracks()[0];
      if (videoTrack) {
        await room.localParticipant.publishTrack(videoTrack, {
          name: 'camera',
          source: Track.Source.Camera,
        });
        console.log('[useLiveKitInterview] ✓ Camera video track published');
      }

      // Publish audio track
      const audioTrack = stream.getAudioTracks()[0];
      if (audioTrack) {
        await room.localParticipant.publishTrack(audioTrack, {
          name: 'microphone',
          source: Track.Source.Microphone,
        });
        console.log('[useLiveKitInterview] ✓ Microphone audio track published');
        setCameraAudioTrack(audioTrack);
      }

      setMediaStatus(prev => ({
        ...prev,
        isCameraPublished: !!videoTrack,
        isAudioPublished: !!audioTrack,
      }));

      return true;
    } catch (error) {
      console.error('[useLiveKitInterview] Failed to publish camera:', error);
      return false;
    }
  }, [room]);

  /**
   * Publish screen share track
   */
  const publishScreen = useCallback(async (): Promise<boolean> => {
    if (!room || room.state !== 'connected') {
      console.error('[useLiveKitInterview] Cannot publish screen: Not connected to room');
      return false;
    }

    try {
      console.log('[useLiveKitInterview] 🖥️  Requesting screen share access...');
      
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          frameRate: { ideal: 10 }
        },
        audio: false
      });

      if (!isMountedRef.current) {
        stream.getTracks().forEach(track => track.stop());
        return false;
      }

      console.log('[useLiveKitInterview] ✓ Screen share access granted');
      
      // Store stream for preview
      screenStreamRef.current = stream;
      
      // Attach to video element for preview
      if (screenVideoRef.current) {
        screenVideoRef.current.srcObject = stream;
        await screenVideoRef.current.play().catch(e => console.warn('Could not play screen preview:', e));
      }

      // Publish screen track
      const screenTrack = stream.getVideoTracks()[0];
      if (screenTrack) {
        // Handle user stopping screen share via browser UI
        screenTrack.onended = () => {
          if (isMountedRef.current) {
            console.log('[useLiveKitInterview] Screen sharing ended via browser UI');
            setMediaStatus(prev => ({ ...prev, isScreenPublished: false }));
            screenStreamRef.current = null;
            if (screenVideoRef.current) {
              screenVideoRef.current.srcObject = null;
            }
          }
        };

        await room.localParticipant.publishTrack(screenTrack, {
          name: 'screen_share',
          source: Track.Source.ScreenShare,
          simulcast: false, // Disable simulcast for faster egress start
        });
        console.log('[useLiveKitInterview] ✓ Screen share track published');
      }

      setMediaStatus(prev => ({ ...prev, isScreenPublished: !!screenTrack }));

      return true;
    } catch (error) {
      console.error('[useLiveKitInterview] Failed to publish screen:', error);
      return false;
    }
  }, [room]);

  /**
   * Stop camera tracks
   */
  const stopCamera = useCallback(() => {
    console.log('[useLiveKitInterview] Stopping camera...');
    
    if (cameraStreamRef.current) {
      cameraStreamRef.current.getTracks().forEach(track => {
        track.stop();
        console.log(`[useLiveKitInterview] Stopped track: ${track.kind}`);
      });
      cameraStreamRef.current = null;
    }

    if (cameraVideoRef.current) {
      cameraVideoRef.current.srcObject = null;
    }

    setCameraAudioTrack(null);
    setMediaStatus(prev => ({
      ...prev,
      isCameraPublished: false,
      isAudioPublished: false,
    }));
  }, []);

  /**
   * Stop screen share tracks
   */
  const stopScreen = useCallback(() => {
    console.log('[useLiveKitInterview] Stopping screen share...');
    
    if (screenStreamRef.current) {
      screenStreamRef.current.getTracks().forEach(track => {
        track.stop();
        console.log(`[useLiveKitInterview] Stopped screen track`);
      });
      screenStreamRef.current = null;
    }

    if (screenVideoRef.current) {
      screenVideoRef.current.srcObject = null;
    }

    setMediaStatus(prev => ({ ...prev, isScreenPublished: false }));
  }, []);

  /**
   * Disconnect from room and stop all tracks
   */
  const disconnect = useCallback(async () => {
    console.log('[useLiveKitInterview] Disconnecting from room...');
    
    stopCamera();
    stopScreen();
    
    await room.disconnect();
    
    setMediaStatus({
      isConnected: false,
      isCameraPublished: false,
      isScreenPublished: false,
      isAudioPublished: false,
    });
    
    console.log('[useLiveKitInterview] ✓ Disconnected from room');
  }, [room, stopCamera, stopScreen]);

  /**
   * Auto-connect if enabled and credentials are available
   */
  useEffect(() => {
    if (autoConnect && livekitUrl && token && roomName && !room.state || room.state === 'disconnected') {
      console.log('[useLiveKitInterview] Auto-connecting to room...');
      connectToRoom();
    }
  }, [autoConnect, livekitUrl, token, roomName, room.state, connectToRoom]);

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      console.log('[useLiveKitInterview] Hook unmounting, cleaning up...');
      if (room.state === 'connected') {
        room.disconnect();
      }
      
      if (cameraStreamRef.current) {
        cameraStreamRef.current.getTracks().forEach(track => track.stop());
      }
      
      if (screenStreamRef.current) {
        screenStreamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, [room]);

  return {
    room,
    mediaStatus,
    cameraAudioTrack,
    cameraVideoRef,
    screenVideoRef,
    connectToRoom,
    publishCamera,
    publishScreen,
    stopCamera,
    stopScreen,
    disconnect,
  };
}

